import {
  Box,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export type QuickFilterOption<T extends string = string> = {
  value: T;
  label: string;
  count?: number;
};

type Props<T extends string = string> = {
  options: QuickFilterOption<T>[];
  value: T | "";
  onChange: (value: T | "") => void;
  showClearButton?: boolean;
  clearButtonLabel?: string;
  allowDeselect?: boolean;
  hideCount?: boolean;
  sx?: React.CSSProperties;
};

export const QuickFilterToggleGroup = <T extends string = string>({
  options,
  value,
  onChange,
  showClearButton = false,
  clearButtonLabel,
  allowDeselect = true,
  hideCount = false,
  sx,
}: Props<T>) => {
  const { t } = useTranslation("common");

  return (
    <Box
      sx={{
        display: "flex",
        direction: "row",
        justifyContent: "space-between",
        alignItems: "center",
        width: "900px",
        ...sx,
      }}
    >
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_, newValue) => {
          if (newValue === null && !allowDeselect) {
            // Keep current value if deselect is not allowed
            return;
          }
          onChange((newValue ?? "") as T | "");
        }}
        sx={{
          height: "48px",
          backgroundColor: "background.light",
          gap: "8px",
          padding: "4px",
          borderRadius: "24px",
          "& .MuiToggleButton-root.Mui-selected": {
            backgroundColor: "primary.light",
            color: "#fff",
          },
          "& .MuiToggleButton-root.Mui-selected:hover": {
            backgroundColor: "hover.primaryLight",
          },
          "& .MuiToggleButton-root.Mui-selected .MuiTypography-root": {
            color: "#fff",
          },
          "& .MuiToggleButton-root": {
            backgroundColor: "transparent",
            borderRadius: "20px",
            border: "none",
            transition: "background-color 200ms ease, color 200ms ease",
          },
          "& .MuiToggleButton-root:hover": {
            backgroundColor: "action.hover",
          },
          "& .MuiToggleButtonGroup-firstButton, & .MuiToggleButtonGroup-middleButton, & .MuiToggleButtonGroup-lastButton":
            {
              borderRadius: "21px",
            },
        }}
      >
        {options.map((option) => (
          <ToggleButton
            key={option.value}
            disableFocusRipple
            disableRipple
            disableTouchRipple
            sx={{
              gap: "12px",
              padding: "7px 16px",
              textTransform: "none",
              border: "none",
              borderRadius: "8px",
            }}
            size="small"
            value={option.value}
          >
            <Typography variant="header" fontSize="16px" fontWeight={700}>
              {option.label}
            </Typography>
            {!hideCount && (
              <Typography
                color={value === option.value ? "#fff" : "text.disabled"}
              >
                {option.count || 0}
              </Typography>
            )}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {showClearButton && (
        <Chip
          label={clearButtonLabel || t("clearSelection")}
          onClick={() => onChange("")}
          variant="text"
          sx={{ marginBottom: "0px", marginRight: "0" }}
        />
      )}
    </Box>
  );
};
